// Signing in, staying signed in, and signing out.
//
// SRP, so the password never crosses the wire, only a proof of it. That is what
// the Cognito app client is configured for and it is why this uses the Cognito
// library rather than posting a password to an endpoint.
import {
  AuthenticationDetails,
  CognitoUser,
  CognitoUserPool,
  type CognitoUserSession,
} from "amazon-cognito-identity-js";
import { COGNITO } from "./config";

const pool = new CognitoUserPool({
  UserPoolId: COGNITO.userPoolId,
  ClientId: COGNITO.clientId,
});

export type SignedIn = {
  email: string;
  /** The Postgres role this person becomes, read from their Cognito group. */
  role: string | null;
  /** The id token. See toSignedIn for why it is this one and not the access token. */
  idToken: string;
};

/** What a token actually says, so a screen can show it rather than guess. */
function readClaims(token: string): Record<string, unknown> {
  const part = token.split(".")[1];
  if (!part) return {};
  const json = atob(part.replace(/-/g, "+").replace(/_/g, "/"));
  try {
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return {};
  }
}

function toSignedIn(session: CognitoUserSession, fallbackEmail: string): SignedIn {
  // The id token, not the access token, and this is not a detail.
  //
  // Cognito's access token carries sub, username and cognito:groups, and no
  // email at all. Both sub and username are the same opaque UUID, because this
  // pool signs people in by email as an alias. So every write attributed
  // through api.caller() came out as 019bd560-b011-70cd-d818-22c742806cbf
  // rather than a person, and api.add_contact_point, which needs a name to put
  // against a fact, refused outright.
  //
  // The id token carries the email, the same cognito:groups PostgREST reads for
  // the role, and an aud claim naming this app client, which the access token
  // has no equivalent of. So this is the token with more identity in it and
  // more that can be checked about it.
  const idToken = session.getIdToken().getJwtToken();
  const claims = readClaims(idToken);
  const email =
    typeof claims["email"] === "string" ? claims["email"] : fallbackEmail;
  const groups = claims["cognito:groups"];
  // The first group is the role, which is the same rule PostgREST applies
  // through jwt-role-claim-key. Somebody in no group has no role to become and
  // every request will be refused, so this reports null rather than inventing
  // a default that would hide the cause.
  const role =
    Array.isArray(groups) && typeof groups[0] === "string" ? groups[0] : null;
  return { email, role, idToken };
}

export class NewPasswordRequired extends Error {
  constructor(readonly user: CognitoUser) {
    super("A new password is required before this account can be used.");
    this.name = "NewPasswordRequired";
  }
}

export function signIn(email: string, password: string): Promise<SignedIn> {
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.authenticateUser(
      new AuthenticationDetails({ Username: email, Password: password }),
      {
        onSuccess: (session) => resolve(toSignedIn(session, email)),
        onFailure: (err: Error) => reject(err),
        // A Cognito-generated temporary password lands here on first use. It is
        // not a failure and it needs its own screen, so it is its own error type
        // rather than a message somebody has to read and interpret.
        newPasswordRequired: () => reject(new NewPasswordRequired(user)),
      },
    );
  });
}

export function completeNewPassword(
  user: CognitoUser,
  email: string,
  password: string,
): Promise<SignedIn> {
  return new Promise((resolve, reject) => {
    user.completeNewPasswordChallenge(password, {}, {
      onSuccess: (session) => resolve(toSignedIn(session, email)),
      onFailure: (err: Error) => reject(err),
    });
  });
}

/* Getting back in, without James.
 *
 * Until now a locked-out person read a sign-in message naming a state nothing
 * in the app could complete, and the way through was James running
 * admin-set-user-password by hand. There are eight accounts, so that was going
 * to happen seven more times.
 *
 * Cognito does both halves on its public API, so neither needs an
 * administrator, a new endpoint or a Lambda. The pool auto-verifies email and
 * deploy/add_user.sh sets email_verified on every account it creates, so every
 * account that exists can receive a code.
 *
 * What this cannot do is rescue an account still on its first temporary
 * password. Cognito refuses a reset in that state, and the existing
 * new-password screen is the path for it. authMessages.ts says so in words.
 */

/** Ask Cognito to email a reset code. */
export function startPasswordReset(email: string): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.forgotPassword({
      // Exactly one of these two fires. Cognito calls inputVerificationCode
      // when it has sent a code and expects one back, and onSuccess when it
      // does not. Both mean the request worked, so both resolve. Leaving
      // inputVerificationCode out makes the library take a different path, so
      // it is defined rather than omitted.
      inputVerificationCode: () => resolve(),
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

/** Set a new password using the emailed code. This signs nobody in. */
export function completePasswordReset(
  email: string,
  code: string,
  password: string,
): Promise<void> {
  const user = new CognitoUser({ Username: email, Pool: pool });
  return new Promise((resolve, reject) => {
    user.confirmPassword(code, password, {
      onSuccess: () => resolve(),
      onFailure: (err: Error) => reject(err),
    });
  });
}

/** The existing session, if there is one and it is still valid. */
export function currentSession(): Promise<SignedIn | null> {
  return new Promise((resolve) => {
    const user = pool.getCurrentUser();
    if (!user) return resolve(null);
    user.getSession((err: Error | null, session: CognitoUserSession | null) => {
      if (err || !session || !session.isValid()) return resolve(null);
      resolve(toSignedIn(session, user.getUsername()));
    });
  });
}

export function signOut(): void {
  pool.getCurrentUser()?.signOut();
}
